import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Icon,
  useColorModeValue,
  Badge,
  Select,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaUser, FaBuilding, FaComments } from 'react-icons/fa';

const Contact: React.FC = () => {
  const bgGradient = useColorModeValue(
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(124,58,237,0.05) 100%)',
    'linear-gradient(180deg, rgba(26,32,44,0) 0%, rgba(124,58,237,0.1) 100%)'
  );

  return (
    <Box py={20} pt="160px" position="relative" overflow="hidden" bgGradient={bgGradient}>
      <Container maxW="1200px" position="relative" zIndex={1}>
        <Box textAlign="center" mb={16}>
          <Badge
            colorScheme="purple"
            mb={4}
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="bold"
          >
            与我们取得联系
          </Badge>
          <Heading
            as="h1"
            size={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            mb={6}
            bgGradient="linear(to-r, brand.purple, purple.500)"
            bgClip="text"
          >
            联系我们
          </Heading>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="800px"
            mx="auto"
            mb={8}
          >
            无论您有任何问题或需求，我们的团队都将竭诚为您服务
          </Text>
        </Box>

        {/* 联系信息与表单区域 */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} mb={20}>
          {/* 联系信息 */}
          <Box>
            <VStack spacing={8} align="start">
              <Heading as="h2" size="xl" color="brand.purple" mb={2}>
                联系方式
              </Heading>
              <Text fontSize="lg" color="gray.600" mb={8}>
                您可以通过以下方式与我们取得联系，我们的团队将在工作日24小时内回复您的咨询。
              </Text>

              {/* 联系信息卡片 */}
              {contactInfo.map((info, index) => (
                <HStack
                  key={index}
                  bg="white"
                  p={4}
                  borderRadius="lg"
                  boxShadow="md"
                  width="100%"
                  spacing={4}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                  }}
                  transition="all 0.3s ease"
                >
                  <Flex
                    bg={info.iconBg}
                    w="50px"
                    h="50px"
                    borderRadius="lg"
                    justify="center"
                    align="center"
                    color="white"
                  >
                    <Icon as={info.icon} boxSize={5} />
                  </Flex>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" color="gray.700">{info.title}</Text>
                    <Text color="gray.600">{info.value}</Text>
                  </VStack>
                </HStack>
              ))}

              {/* 办公时间卡片 */}
              <Box
                bg="white"
                p={6}
                borderRadius="lg"
                boxShadow="md"
                width="100%"
                borderLeft="4px solid"
                borderLeftColor="brand.purple"
              >
                <Heading as="h3" size="md" mb={4} color="brand.purple">
                  办公时间
                </Heading>
                <VStack align="start" spacing={2}>
                  <HStack justify="space-between" width="100%">
                    <Text fontWeight="medium">周一至周五:</Text>
                    <Text>09:00 - 18:00</Text>
                  </HStack>
                  <HStack justify="space-between" width="100%">
                    <Text fontWeight="medium">周六:</Text>
                    <Text>10:00 - 16:00</Text>
                  </HStack>
                  <HStack justify="space-between" width="100%">
                    <Text fontWeight="medium">周日:</Text>
                    <Text>休息</Text>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* 联系表单 */}
          <Box
            bg="white"
            p={{ base: 6, md: 8 }}
            borderRadius="xl"
            boxShadow="xl"
            position="relative"
          >
            <Box
              position="absolute"
              top="-20px"
              right="-20px"
              bg="brand.purple"
              width="120px"
              height="120px"
              opacity="0.05"
              borderRadius="full"
              zIndex={-1}
            />

            <VStack spacing={6} align="stretch">
              <Heading as="h2" size="lg" color="brand.purple" mb={2}>
                发送消息
              </Heading>
              <Text color="gray.600" mb={4}>
                请填写下面的表单，我们将尽快与您联系
              </Text>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>姓名</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaUser} color="gray.300" />
                    </InputLeftElement>
                    <Input type="text" placeholder="您的姓名" />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>公司</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaBuilding} color="gray.300" />
                    </InputLeftElement>
                    <Input type="text" placeholder="您的公司" />
                  </InputGroup>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>电子邮箱</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaEnvelope} color="gray.300" />
                    </InputLeftElement>
                    <Input type="email" placeholder="您的邮箱" />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>电话</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaPhone} color="gray.300" />
                    </InputLeftElement>
                    <Input type="tel" placeholder="您的电话" />
                  </InputGroup>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel>咨询类型</FormLabel>
                <Select placeholder="请选择咨询类型" icon={<FaComments />}>
                  <option value="solution">解决方案咨询</option>
                  <option value="product">产品咨询</option>
                  <option value="cooperation">商务合作</option>
                  <option value="support">技术支持</option>
                  <option value="other">其他</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>消息内容</FormLabel>
                <Textarea
                  placeholder="请详细描述您的需求或问题..."
                  rows={5}
                  resize="none"
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="purple"
                size="lg"
                fontSize="md"
                fontWeight="bold"
                px={10}
                py={6}
                mt={4}
                bgGradient="linear(to-r, brand.purple, purple.600)"
                _hover={{
                  bgGradient: "linear(to-r, purple.700, purple.800)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 15px -3px rgba(124, 58, 237, 0.4)"
                }}
              >
                提交信息
              </Button>
            </VStack>
          </Box>
        </SimpleGrid>

      </Container>
    </Box>
  );
};

const contactInfo = [
  {
    title: '公司电话',
    value: '18617190008',
    icon: FaPhone,
    iconBg: 'brand.purple',
  },
  {
    title: '业务邮箱',
    value: 'y_square_biz@163.com',
    icon: FaEnvelope,
    iconBg: 'brand.blue',
  },
  {
    title: '技术支持',
    value: 'y_square_biz@163.com',
    icon: FaEnvelope,
    iconBg: 'brand.cyan',
  },
  {
    title: '公司地址',
    value: '深圳市龙华区民治街道上芬社区第五工业区一区28号303',
    icon: FaMapMarkerAlt,
    iconBg: 'purple.600',
  },
];

export default Contact;